import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Badge, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import PlayerLink from "../components/common/PlayerLink";
import PlayerPicker from "../components/common/PlayerPicker";
import GquestDetail from "../components/gquests/GquestDetail";
import { Title } from "../components/common/Title";
import PaginationControls from "../components/boards/PaginationControls";
import { formatDate } from "../components/epicLeaderboard/format";
import { useMyPlayer } from "../context/MyPlayerContext";
import { playerPath } from "../utils/playerLink";
import type {
  GquestFastestRow,
  GquestMe,
  GquestResponse,
  GquestStanding,
  GquestWinnerRow
} from "../types/gquests";
import { useComponentTracking, useTimeTracking } from "../utils/analytics";
import "../components/epicLeaderboard/epicLeaderboard.css";
import "./gquests.css";

const DEFAULT_PAGE_LIMIT = 10;
const PAGE_LIMIT_OPTIONS = [10, 20, 50, 100];
const DEFAULT_BOARD_LIMIT = 10;
const MAX_BOARD_LIMIT = 300;
const BOARD_LIMIT_OPTIONS = [10, 25, 50, 100, 300];
const QUEST_TYPE_LABELS: Record<string, string> = {
  LT25: "Under 25 wins",
  "25_199": "25–199 wins",
  "200_PLUS": "200+ wins"
};

const positiveInteger = (value: string | null): number | undefined =>
  value && /^\d+$/.test(value) && Number(value) > 0 ? Number(value) : undefined;

const pageLimit = (value: string | null) => {
  const parsed = positiveInteger(value);
  return parsed && PAGE_LIMIT_OPTIONS.includes(parsed)
    ? parsed
    : DEFAULT_PAGE_LIMIT;
};

const boardLimitParam = (value: string | null) => {
  const parsed = positiveInteger(value);
  return parsed && BOARD_LIMIT_OPTIONS.includes(parsed)
    ? parsed
    : DEFAULT_BOARD_LIMIT;
};

const rangeLabel = (min: number, max: number) =>
  min === max ? String(min) : `${min}–${max}`;

const durationTier = (seconds: number) =>
  seconds <= 180 ? "fast" : seconds <= 360 ? "standard" : "slow";

const GquestTile = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="epic-lb-card gquest-tile">
    <div className="epic-lb-card-title">{label}</div>
    <div className="epic-lb-tile-value">{value}</div>
    {sub && <div className="epic-lb-muted">{sub}</div>}
  </div>
);

const Gquests = () => {
  useComponentTracking("Gquests");
  useTimeTracking("Gquests");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<GquestResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [openGquestId, setOpenGquestId] = useState<number | null>(null);
  const { myPlayer, setMyPlayer } = useMyPlayer();
  const navigate = useNavigate();
  const [boardFilter, setBoardFilter] = useState("");

  const rangeId = positiveInteger(searchParams.get("rangeId"));
  const status = searchParams.get("status") ?? "";
  const questType = searchParams.get("questType") ?? "";
  const startDate = searchParams.get("startDate") ?? "";
  const endDate = searchParams.get("endDate") ?? "";
  const page = positiveInteger(searchParams.get("page")) ?? 1;
  const limit = pageLimit(searchParams.get("limit"));
  const boardLimit = boardLimitParam(searchParams.get("boardLimit"));
  // Finding a name searches the whole board, so ask for all of it.
  const boardQuery = boardFilter.trim().toLowerCase();
  const requestBoardLimit = boardQuery ? MAX_BOARD_LIMIT : boardLimit;

  const updateParams = useCallback(
    (changes: Record<string, string | undefined>, resetPage = true) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        Object.entries(changes).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        if (resetPage && !("page" in changes)) next.delete("page");
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const controller = new AbortController();
    const params: Record<string, string | number> = { page, limit };
    if (rangeId) params.rangeId = rangeId;
    if (status) params.status = status;
    if (questType) params.questType = questType;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (requestBoardLimit !== DEFAULT_BOARD_LIMIT) params.boardLimit = requestBoardLimit;
    if (myPlayer) params.player = myPlayer;

    setLoading(true);
    setError(false);
    axios
      .get<GquestResponse>(`${process.env.REACT_APP_API}public/gquests`, {
        params,
        signal: controller.signal
      })
      .then((response) => {
        if (!controller.signal.aborted) setData(response.data);
      })
      .catch((requestError) => {
        if (axios.isCancel(requestError) || controller.signal.aborted) return;
        console.error("Failed to fetch global quests", requestError);
        setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [page, limit, requestBoardLimit, myPlayer, rangeId, status, questType, startDate, endDate, reload]);

  const rows = data?.rows ?? [];
  const summary = data?.summary;
  const mine = myPlayer ? foundMe(data?.me) : null;

  return (
    <Container className="py-4">
      <Title text="Global Quests" />
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <p className="text-light mb-0">
          Parsed global quest results, winners, players, and completion times.
        </p>
        <div className="gquest-lookup">
          <PlayerPicker
            ariaLabel="Go to a player's GQ stats"
            placeholder="Go to a player's GQ stats"
            submitLabel="View stats →"
            onPick={(name) => navigate(playerPath({ player: name, tab: "gquests" }))}
          />
        </div>
      </div>

      <Form onSubmit={(event) => event.preventDefault()} className="mb-4 gquest-filters">
        <Row className="g-3 text-light">
          <Col xs={12} md={3}>
            <Form.Label>Level range</Form.Label>
            <Form.Select
              value={rangeId ?? ""}
              onChange={(event) => updateParams({ rangeId: event.target.value || undefined })}
            >
              <option value="">All ranges</option>
              {data?.ranges.map((range) => (
                <option key={range.id} value={range.id}>
                  {rangeLabel(range.levelMin, range.levelMax)}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label>Quest status</Form.Label>
            <Form.Select value={status} onChange={(event) => updateParams({ status: event.target.value || undefined })}>
              <option value="">All statuses</option>
              <option value="Finished">Finished</option>
              <option value="Extended">Extended</option>
            </Form.Select>
          </Col>
          <Col xs={6} md={3}>
            <Form.Label>Quest type</Form.Label>
            <Form.Select value={questType} onChange={(event) => updateParams({ questType: event.target.value || undefined })}>
              <option value="">All quest types</option>
              {Object.entries(QUEST_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={4}>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Label>From</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(event) => updateParams({ startDate: event.target.value || undefined })} />
              </Col>
              <Col xs={6}>
                <Form.Label>To</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(event) => updateParams({ endDate: event.target.value || undefined })} />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      {error && (
        <div className="epic-lb-card text-center mb-4">
          <div className="mb-2">Couldn&apos;t load global quest data.</div>
          <Button size="sm" variant="outline-light" onClick={() => setReload((value) => value + 1)}>Retry</Button>
        </div>
      )}

      {loading && !data ? (
        <div className="text-center my-5"><Spinner animation="border" variant="light" /></div>
      ) : data && summary && !error && (
        <div className={loading ? "epic-lb-refetching" : undefined}>
          <Row className="g-3 mb-4">
            <Col xs={6} md={4} lg><GquestTile label="GQs recorded" value={summary.totalGquests.toLocaleString()} sub={mine ? `You entered ${mine.entries.toLocaleString()} (${summary.totalGquests ? Math.round((mine.entries / summary.totalGquests) * 1000) / 10 : 0}%)` : undefined} /></Col>
            <Col xs={6} md={4} lg><GquestTile label="Unique winners" value={summary.uniqueWinners.toLocaleString()} /></Col>
            <Col xs={6} md={4} lg><GquestTile label="Median duration" value={summary.medianDuration ?? "—"} sub={mine?.medianDuration ? `You: ${mine.medianDuration}` : undefined} /></Col>
            <Col xs={6} md={4} lg><GquestTile label="Average duration" value={summary.averageDuration ?? "—"} /></Col>
            <Col xs={6} md={4} lg><GquestTile label="Extended GQs" value={`${summary.extendedPct}%`} sub={`${summary.extendedGquests.toLocaleString()} completed`} /></Col>
            <Col xs={6} md={4} lg><GquestTile label="Avg. players" value={summary.averageEntrants} sub="per global quest" /></Col>
          </Row>

          {myPlayer ? data.me && <GquestYouCard me={data.me} data={data} /> : <GquestSetPlayerCard onPick={setMyPlayer} />}

          <Row className="g-3 mb-4">
            <Col md={6}><GquestRecordCard label="Fastest GQ" record={summary.fastestGquest} mine={mine?.fastestWin ? `Your best: ${mine.fastestWin.duration}${summary.fastestGquest ? (mine.fastestWin.durationSec === summary.fastestGquest.durationSec ? " · ties it" : ` · +${durationGap(mine.fastestWin.durationSec - summary.fastestGquest.durationSec)}`) : ""}` : undefined} /></Col>
            <Col md={6}><GquestRecordCard label="Longest GQ" record={summary.longestGquest} mine={mine?.longestWin ? `Your longest: ${mine.longestWin.duration}${summary.longestGquest ? (mine.longestWin.durationSec === summary.longestGquest.durationSec ? " · ties it" : ` · −${durationGap(summary.longestGquest.durationSec - mine.longestWin.durationSec)}`) : ""}` : undefined} /></Col>
          </Row>

          <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3 text-light">
            <div className="epic-lb-card-title gquest-history-title">Leaderboards</div>
            <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <Form.Label htmlFor="gquest-board-filter" className="mb-1">Find in the leaderboards</Form.Label>
              <Form.Control
                id="gquest-board-filter"
                type="text"
                className="player-picker-input gquest-board-filter"
                value={boardFilter}
                onChange={(event) => setBoardFilter(event.target.value)}
                placeholder="Searches the top 300"
              />
            </div>
            <div>
              <Form.Label htmlFor="gquest-board-limit" className="mb-1">Leaderboard size</Form.Label>
              <Form.Select
                id="gquest-board-limit"
                className="gquest-page-size"
                value={boardLimit}
                disabled={!!boardQuery}
                onChange={(event) => updateParams({ boardLimit: event.target.value === String(DEFAULT_BOARD_LIMIT) ? undefined : event.target.value }, false)}
              >
                {BOARD_LIMIT_OPTIONS.map((option) => <option key={option} value={option}>Top {option}</option>)}
              </Form.Select>
            </div>
            </div>
          </div>
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <div className="epic-lb-card">
                <div className="epic-lb-card-title">Top winners</div>
                <BoardCaption me={boardQuery ? null : mine} board="winners" shown={data.topWinners.length} />
                <GqLeaderboardTable
                  headings={["#", "Player", "Wins", "Entries", "Win rate"]}
                  rows={(boardQuery
                    ? data.topWinners.filter((row) => row.player.toLowerCase().includes(boardQuery))
                    : withNearby(data.topWinners, data.me, "winners")
                  ).map(toWinnerCells)}
                  empty={boardQuery ? "No match in the top 300." : undefined}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="epic-lb-card">
                <div className="epic-lb-card-title">Fastest GQers</div>
                <div className="epic-lb-muted mb-2">Median winning duration; at least {data.fastestMinWins} wins.</div>
                <BoardCaption me={boardQuery ? null : mine} board="fastest" shown={data.fastestGqers.length} />
                <GqLeaderboardTable
                  headings={["#", "Player", "Median", "Average", "Wins"]}
                  rows={(boardQuery
                    ? data.fastestGqers.filter((row) => row.player.toLowerCase().includes(boardQuery))
                    : withNearby(data.fastestGqers, data.me, "fastest")
                  ).map(toFastestCells)}
                  empty={boardQuery ? "No match in the top 300." : "No players meet the minimum-win threshold."}
                />
              </div>
            </Col>
          </Row>

          <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
            <div className="epic-lb-card-title gquest-history-title">GQ history</div>
            <Form.Select
              aria-label="Rows per page"
              className="gquest-page-size"
              value={limit}
              onChange={(event) => updateParams({ limit: event.target.value }, true)}
            >
              {PAGE_LIMIT_OPTIONS.map((option) => <option key={option} value={option}>{option} per page</option>)}
            </Form.Select>
          </div>
          {rows.length === 0 ? (
            <div className="epic-lb-card text-center">No global quests match these filters.</div>
          ) : (
            <div className="epic-lb-table-scroll">
              <table className="epic-lb-table gquest-history-table">
                <colgroup>
                  <col className="gquest-col-completed" /><col className="gquest-col-range" />
                  <col className="gquest-col-type" /><col className="gquest-col-duration" />
                  <col className="gquest-col-status" /><col className="gquest-col-winner" />
                  <col className="gquest-col-players" /><col className="gquest-col-targets" />
                </colgroup>
                <thead><tr><th>Completed</th><th>Range</th><th>GQuest type</th><th>Duration</th><th>Status</th><th>Winner</th><th>Players</th><th>Targets</th></tr></thead>
                <tbody>
                  {rows.map((row) => {
                    const open = openGquestId === row.id;
                    return <Fragment key={row.id}>
                      <tr>
                        <td><div>{formatDate(row.endedAt)}</div><div className="gquest-number">GQ #{row.gqNumber.toLocaleString()}</div></td>
                        <td>
                          <Badge bg="transparent" pill className="gquest-range-badge">
                            <span>Lvl</span> {rangeLabel(row.range.levelMin, row.range.levelMax)}
                          </Badge>
                        </td>
                        <td><span className={`gquest-quest-type gquest-quest-type--${row.questType.toLowerCase()}`}>{QUEST_TYPE_LABELS[row.questType] ?? row.questType}</span></td>
                        <td className={`fw-bold gquest-duration gquest-duration--${durationTier(row.durationSec)}`}>{row.duration}</td>
                        <td><span className={`gquest-status gquest-status--${row.status.toLowerCase()}`}>{row.status}</span></td>
                        <td><PlayerLink player={row.winner} tab="gquests" className="epic-lb-player-link" /></td>
                        <td>{row.entrants}</td>
                        <td>
                          <button
                            type="button"
                            className="gquest-target-toggle"
                            onClick={() => setOpenGquestId(open ? null : row.id)}
                            aria-expanded={open}
                          >
                            {open ? "Hide" : "Show"} {row.mobTargets}
                          </button>
                        </td>
                      </tr>
                      {open && <tr className="gquest-mobs-row">
                        <td colSpan={8}>
                          <GquestDetail row={row} />
                        </td>
                      </tr>}
                    </Fragment>;
                  })}
                </tbody>
              </table>
            </div>
          )}
          {data.pagination.totalPages > 1 && (
            <PaginationControls pagination={data.pagination} onPageChange={(nextPage) => updateParams({ page: String(nextPage) }, false)} />
          )}
        </div>
      )}
    </Container>
  );
};

type FoundMe = Extract<GquestMe, { found: true }>;
const foundMe = (me: GquestMe | null | undefined): FoundMe | null =>
  me && me.found ? me : null;

// One line under a leaderboard title saying where the viewer stands. Nothing
// when their row is already among the rows shown.
const BoardCaption = ({ me, board, shown }: { me: FoundMe | null; board: "winners" | "fastest"; shown: number }) => {
  if (!me) return null;
  const standing = me[board];
  let text: string | null = null;
  if (standing) {
    if (standing.rank > shown) {
      text = `You: #${standing.rank.toLocaleString()} of ${standing.total.toLocaleString()} · top ${standing.topPercent}%`;
    }
  } else if (board === "fastest" && me.fastestWinsNeeded > 0) {
    text = `You're not ranked yet: ${me.fastestWinsNeeded} more win(s) to qualify`;
  } else {
    text = "You haven't won a GQ with these filters";
  }
  return text ? <div className="gquest-mine mb-2">{text}</div> : null;
};

const GAP = "gap";
type Cells = (string | number | JSX.Element)[];
type TableRow = Cells | typeof GAP;

const playerCell = (player: string) => (
  <PlayerLink key={player} player={player} tab="gquests" className="epic-lb-player-link" />
);

const toWinnerCells = (row: GquestWinnerRow | typeof GAP): TableRow =>
  row === GAP ? GAP : [row.rank, playerCell(row.player), row.wins, row.entries, `${row.winRate}%`];

const toFastestCells = (row: GquestFastestRow | typeof GAP): TableRow =>
  row === GAP ? GAP : [row.rank, playerCell(row.player), row.medianDuration, row.averageDuration, row.wins];

// The visible top rows, plus the viewer's own place and its neighbours when
// they rank below the visible slice.
function withNearby<T extends { rank: number }>(
  top: T[],
  me: GquestMe | null,
  board: "winners" | "fastest"
): (T | typeof GAP)[] {
  if (!me || !me.found) return top;
  const standing = me[board] as unknown as GquestStanding<T> | null;
  if (!standing || standing.rank <= top.length) return top;
  const below = standing.nearby.filter((row) => row.rank > top.length);
  const contiguous = below[0]?.rank === top.length + 1;
  return [...top, ...(contiguous ? [] : [GAP as typeof GAP]), ...below];
}

const durationGap = (seconds: number) => {
  const abs = Math.abs(seconds);
  return `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
};

const GquestSetPlayerCard = ({ onPick }: { onPick: (name: string) => void }) => (
  <div className="epic-lb-card mb-4 gquest-you">
    <div className="epic-lb-card-title">★ Where do you rank?</div>
    <div className="epic-lb-muted mb-2">
      Enter your character to see your rank and how far you are from the next spot,
      and to be highlighted across the site. Saved in this browser only.
    </div>
    <PlayerPicker onPick={onPick} placeholder="Your character name (3 characters minimum)" ariaLabel="Your character name" />
  </div>
);

const GquestYouCard = ({ me, data }: { me: GquestMe; data: GquestResponse }) => {
  if (!me.found) {
    return (
      <div className="epic-lb-card mb-4 gquest-you">
        <div className="epic-lb-card-title">★ {me.player}</div>
        <div className="epic-lb-muted">No global quest results for {me.player} with these filters.</div>
      </div>
    );
  }
  const winners = me.winners;
  const above = winners?.nearby.find((row) => row.rank === winners.rank - 1);
  const tenth = data.topWinners[9];
  const fastest = me.fastest;
  const myFastestRow = fastest?.nearby.find((row) => row.rank === fastest.rank);
  const fastestAbove = fastest?.nearby.find((row) => row.rank === fastest.rank - 1);
  const tenthFastest = data.fastestGqers[9];

  return (
    <div className="epic-lb-card mb-4 gquest-you">
      <div className="epic-lb-card-title">
        <PlayerLink player={me.player} tab="gquests" className="epic-lb-player-link" /> vs the field
      </div>
      <Row className="g-3">
        <Col xs={6} md={3}>
          <div className="epic-lb-muted">Top winners</div>
          <div className="epic-lb-tile-value">{winners ? `#${winners.rank}` : "—"}</div>
          <div className="epic-lb-muted">
            {winners ? `of ${winners.total.toLocaleString()} · top ${winners.topPercent}%` : "No wins yet"}
          </div>
          {winners && above && <div className="epic-lb-muted">{above.wins === me.wins ? `Tied on wins with #${above.rank}` : `${above.wins - me.wins} wins behind #${above.rank}`}</div>}
          {winners && tenth && winners.rank > 10 && <div className="epic-lb-muted">{tenth.wins - me.wins} wins behind #10</div>}
        </Col>
        <Col xs={6} md={3}>
          <div className="epic-lb-muted">Fastest GQers</div>
          <div className="epic-lb-tile-value">{fastest ? `#${fastest.rank}` : "—"}</div>
          <div className="epic-lb-muted">
            {fastest
              ? `of ${fastest.total.toLocaleString()} · top ${fastest.topPercent}%`
              : me.fastestWinsNeeded > 0
                ? `${me.fastestWinsNeeded} more win(s) to qualify`
                : "No wins yet"}
          </div>
          {myFastestRow && fastestAbove && <div className="epic-lb-muted">{durationGap(myFastestRow.medianDurationSec - fastestAbove.medianDurationSec)} slower than #{fastestAbove.rank}</div>}
          {myFastestRow && tenthFastest && fastest && fastest.rank > 10 && <div className="epic-lb-muted">{durationGap(myFastestRow.medianDurationSec - tenthFastest.medianDurationSec)} off #10</div>}
        </Col>
        <Col xs={6} md={3}>
          <div className="epic-lb-muted">Record</div>
          <div className="epic-lb-tile-value">{me.wins} / {me.entries}</div>
          <div className="epic-lb-muted">wins / entries · {me.winRate}%</div>
          <div className="epic-lb-muted">Median win {me.medianDuration ?? "—"}</div>
        </Col>
        <Col xs={6} md={3}>
          <div className="epic-lb-muted">Fastest win</div>
          <div className="epic-lb-tile-value">{me.fastestWin?.duration ?? "—"}</div>
          <div className="epic-lb-muted">
            {me.fastestWin
              ? `GQ #${me.fastestWin.gqNumber.toLocaleString()} · ${rangeLabel(me.fastestWin.levelMin, me.fastestWin.levelMax)}`
              : "No wins in these filters"}
          </div>
        </Col>
      </Row>
    </div>
  );
};

const GquestRecordCard = ({ label, record, mine }: { label: string; record: GquestResponse["summary"]["fastestGquest"]; mine?: string }) => (
  <div className="epic-lb-card gquest-record-card">
    <div className="epic-lb-card-title">{label}</div>
    {record ? <>
      <div className="epic-lb-tile-value">{record.duration}</div>
      <div className="epic-lb-muted">GQ #{record.gqNumber.toLocaleString()} · {rangeLabel(record.levelMin, record.levelMax)} · {formatDate(record.endedAt)}</div>
      <div className="mt-1"><PlayerLink player={record.winner} tab="gquests" className="epic-lb-player-link" /></div>
      {mine && <div className="mt-1 gquest-mine">{mine}</div>}
    </> : <div className="epic-lb-muted">No matching GQs.</div>}
  </div>
);

const GqLeaderboardTable = ({ headings, rows, empty = "No results." }: { headings: string[]; rows: TableRow[]; empty?: string }) => (
  <div className="gquest-leaderboard-scroll">
    <table className="gquest-leaderboard-table">
      <colgroup>
        <col className="gquest-lb-col-rank" />
        <col />
        {headings.slice(2).map((heading) => <col key={heading} className="gquest-lb-col-stat" />)}
      </colgroup>
      <thead><tr>{headings.map((heading) => <th key={heading}>{heading}</th>)}</tr></thead>
      <tbody>
        {rows.length ? rows.map((row, rowIndex) => row === GAP ? <tr key={rowIndex}><td colSpan={headings.length} className="epic-lb-muted gquest-gap">···</td></tr> : <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>) : <tr><td colSpan={headings.length} className="epic-lb-muted">{empty}</td></tr>}
      </tbody>
    </table>
  </div>
);

export default Gquests;
